import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n";
import { api, errMsg, fmtTime, fmtDate } from "@/lib/api";
import { Card, Loader, PageTitle, PositionBadge, TimeInput } from "@/components/kv";
import { ReqStatus } from "@/components/RequestBox";

const PendingActions = ({ r, onDone }) => {
  const { t } = useT();
  const [cin, setCin] = useState(r.requested_in || "");
  const [cout, setCout] = useState(r.requested_out || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (kind) => {
    setBusy(true);
    try {
      const body = kind === "approve" ? { clock_in: cin || null, clock_out: cout || null, admin_note: note } : { admin_note: note };
      await api.post(`/admin/requests/${r.id}/${kind}`, body);
      toast.success(t(kind === "approve" ? "req.approved_toast" : "req.rejected_toast"));
      window.dispatchEvent(new Event("kvp-requests-changed"));
      onDone();
    } catch (e) {
      toast.error(errMsg(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 space-y-3 rounded-xl bg-[#FAF6F0] p-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">{t("req.apply_in")}</Label><TimeInput value={cin} onChange={setCin} className="bg-white font-mono-kv w-full" data-testid={`req-apply-in-${r.id}`} /></div>
        <div className="space-y-1"><Label className="text-xs">{t("req.apply_out")}</Label><TimeInput value={cout} onChange={setCout} className="bg-white font-mono-kv w-full" data-testid={`req-apply-out-${r.id}`} /></div>
      </div>
      <p className="text-[11px] text-stone-500">{t("req.apply_hint")}</p>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("req.note_ph")} maxLength={300} className="bg-white" data-testid={`req-note-${r.id}`} />
      <div className="flex gap-2">
        <Button className="rounded-full bg-green-700 hover:bg-green-800" disabled={busy} onClick={() => act("approve")} data-testid={`req-approve-${r.id}`}><Check className="h-4 w-4 mr-1" />{t("req.approve")}</Button>
        <Button variant="outline" className="rounded-full text-red-700 border-red-200 hover:bg-red-50" disabled={busy} onClick={() => act("reject")} data-testid={`req-reject-${r.id}`}><X className="h-4 w-4 mr-1" />{t("req.reject")}</Button>
      </div>
    </div>
  );
};

const RequestCard = ({ r, onDone }) => {
  const { t, lang } = useT();
  return (
    <Card className="p-5" data-testid={`request-card-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-semibold">{r.name}</div>
          <div className="flex items-center gap-2 mt-1"><PositionBadge position={r.position} /><span className="text-xs text-stone-500 capitalize">{fmtDate(r.date, lang)}</span></div>
        </div>
        <ReqStatus status={r.status} testid={`request-status-${r.id}`} />
      </div>
      <p className="mt-3 text-stone-800 break-words">“{r.message}”</p>
      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
        <div><div className="text-xs text-stone-500">{t("req.requested")}</div><div className="font-mono-kv">{r.requested_in || "—"} – {r.requested_out || "—"}</div></div>
        <div><div className="text-xs text-stone-500">{t("req.current")}</div>
          <div className="font-mono-kv">{r.record ? `${fmtTime(r.record.clock_in)} – ${r.record.clock_out ? fmtTime(r.record.clock_out) : "…"}` : t("req.no_record")}</div>
        </div>
      </div>
      {r.status === "pending" ? <PendingActions r={r} onDone={onDone} /> : r.admin_note && <p className="text-xs text-stone-500 mt-3 italic">{t("req.admin_note")}: {r.admin_note}</p>}
    </Card>
  );
};

export default function Requests() {
  const { t } = useT();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/requests", { params: { status: tab } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e, t)));
  }, [tab, t]);
  useEffect(() => {
    setItems(null);
    load();
  }, [load]);

  return (
    <div>
      <PageTitle kicker={t("adminnav.requests")} title={t("req.title")}>
        {["pending", "all"].map((k) => (
          <Button key={k} variant={tab === k ? "default" : "outline"} className="rounded-full" onClick={() => setTab(k)} data-testid={`requests-tab-${k}`}>{t(`req.tab_${k}`)}</Button>
        ))}
      </PageTitle>
      {!items ? <Loader /> : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="requests-list">
          {items.length === 0 && <p className="text-stone-500 text-sm">{t("req.none")}</p>}
          {items.map((r) => <RequestCard key={r.id} r={r} onDone={load} />)}
        </div>
      )}
    </div>
  );
}
